import React, { useEffect, useState, useRef } from 'react';
import { db } from './firebaseConfig';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  setDoc,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

function ChatModal({ session, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [newMessageIds, setNewMessageIds] = useState([]);
  const bottomRef = useRef();
  const typingRef = useRef();

  useEffect(() => {
    const q = query(
      collection(db, 'sessions', session.id, 'messages'),
      orderBy('createdAt')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const addedIds = snapshot.docChanges()
        .filter(change => change.type === 'added' && change.doc.data().uid !== user.uid)
        .map(change => change.doc.id);

      if (addedIds.length > 0) {
        setNewMessageIds(prev => [...prev, ...addedIds]);
        setTimeout(() => {
          setNewMessageIds(prev => prev.filter(id => !addedIds.includes(id)));
        }, 2500); // highlight duration
      }

      setMessages(msgs);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubscribe();
  }, [session.id, user.uid]);

  useEffect(() => {
    const typingCol = collection(db, 'sessions', session.id, 'typing');
    const unsubTyping = onSnapshot(typingCol, (snapshot) => {
      const typing = snapshot.docs
        .map(doc => doc.data().name || doc.id)
        .filter(name => name !== user.displayName);
      setTypingUsers(typing);
    });
    return () => unsubTyping();
  }, [session.id, user.displayName]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (typingRef.current) {
        deleteDoc(doc(db, 'sessions', session.id, 'typing', user.uid));
        typingRef.current = false;
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [input, session.id, user.uid]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    await addDoc(collection(db, 'sessions', session.id, 'messages'), {
      text: input,
      sender: user.displayName,
      uid: user.uid,
      createdAt: new Date()
    });
    setInput('');
    deleteDoc(doc(db, 'sessions', session.id, 'typing', user.uid));
    typingRef.current = false;
  };

  const handleTyping = async (e) => {
    setInput(e.target.value);
    if (!typingRef.current) {
      await setDoc(doc(db, 'sessions', session.id, 'typing', user.uid), {
        name: user.displayName
      });
      typingRef.current = true;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[1000] backdrop-blur-sm">
      <div className="bg-white rounded-xl w-[90%] max-w-[400px] h-[500px] flex flex-col overflow-hidden shadow-2xl">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
          <strong className="truncate pr-4 text-gray-800">
            💬 Chat: {session.name}
            {(session.date || session.scheduledStartTime) && (
              <span className="text-xs font-normal text-gray-500 ml-2">
                ({new Date(session.date || (session.scheduledStartTime.toDate ? session.scheduledStartTime.toDate() : session.scheduledStartTime)).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })})
              </span>
            )}
          </strong>
          <button
            onClick={onClose}
            className="border-none bg-transparent cursor-pointer text-xl text-gray-400 hover:text-gray-700 transition"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-3 bg-gray-50/50">
          {messages.length === 0 && (
            <p className="text-xs text-gray-500 italic mb-2 text-center bg-gray-100 py-2 rounded-lg">
              This chat is public for anyone interested in this meetup 👋
            </p>
          )}

          {messages.map(msg => (
            <div
              key={msg.id}
              className={`p-3 rounded-2xl max-w-[75%] shadow-sm ${newMessageIds.includes(msg.id) ? 'ring-2 ring-yellow-400 ring-offset-1 bg-yellow-50 transition-all' : ''} ${msg.uid === user.uid
                ? 'self-end bg-indigo-600 text-white rounded-br-sm'
                : 'self-start bg-white border border-gray-100 text-gray-800 rounded-bl-sm'
                }`}
            >
              <div className="text-[11px] font-medium opacity-80 mb-0.5">{msg.sender}</div>
              <div className="text-sm leading-relaxed">{msg.text}</div>
            </div>
          ))}

          {typingUsers.length > 0 && (
            <div className="text-xs italic text-gray-400 animate-pulse ml-2 mt-1">
              {typingUsers.length === 1
                ? `${typingUsers[0]} is typing...`
                : `${typingUsers.join(', ')} are typing...`}
            </div>
          )}

          <div ref={bottomRef}></div>
        </div>

        <form onSubmit={handleSend} className="flex border-t border-gray-100 p-3 bg-white">
          <input
            value={input}
            onChange={handleTyping}
            placeholder="Type a message..."
            className="flex-1 p-2.5 border border-gray-200 bg-gray-50 rounded-lg mr-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
          />
          <button
            type="submit"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg cursor-pointer transition-colors shadow-sm"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatModal;
